import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useRef, useState } from "react"


import { LoadMoreButton } from "@/components/Chat/LoadMoreButton.tsx"
import { MessageText } from "@/components/Chat/MessageStyles.ts"
import { StyledUserMessageBalloon, UserMessageContainer, UserMessageType } from "@/components/Chat/UserMessageStyles.ts"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { Gap, Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { createLLMLogChatModel } from "@/components/LLMLogChat/LLMLogChatModel.ts"
import { LLMLogPagesList } from "@/components/LLMLogChat/LLMLogPagesList.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { usePersistence, useQueries } from "@/hooks/useRootContext.ts"
import { CodeFileMedium, HelpLight, Icon16, MoreRegular } from "@/icons/Icons.ts"
import { formatDateTime, formatDuration } from "@/lib/date.ts"
import { creditsToDollars, formatDollars, ProductName } from "@/lib/displayUtils.ts"
import { recordLength } from "@/lib/std.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { Characters, ellipsize } from "@/styles/textStyles.ts"

import type { LLMLogModel } from "@/components/LLMLogChat/LLMLogChatModel.ts"
import type { ScrollableModel } from "@/components/Scrollable/ScrollableModel.ts"
import type { LlmTokenStats } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export const LLMLogChat = observer((props: {
  model: TaskViewModel
  scrollableModel: ScrollableModel
}) => {
  const task = props.model.task.get()
  const taskId = task?.id
  const queries = useQueries()
  const persistence = usePersistence()

  const loadMorePressed = useRef(false)
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)

  const model = useModel(() => {
    return taskId && selectedPageId ? createLLMLogChatModel(taskId, queries, persistence, selectedPageId) : null
  }, [taskId, selectedPageId])

  useEffect(() => {
    if (!model) return

    if (!loadMorePressed.current && !model.disableAutoscroll.get()) {
      props.scrollableModel.scrollToBottom("instant")
    }

    loadMorePressed.current = false
  }, [model, props.scrollableModel])

  if (!task) {
    return null
  }

  // Show pages list if no page is selected
  if (!selectedPageId) {
    return (
      <Container>
        <LLMLogPagesList
          model={props.model}
          scrollableModel={props.scrollableModel}
          onPageSelect={setSelectedPageId}
          selectedPageId={selectedPageId}
        />
      </Container>
    )
  }

  // Show log entries for selected page
  if (!model) {
    return (
      <Container>
        <Spinner text={ellipsize("Loading")} />
      </Container>
    )
  }

  return (
    <Container>
      <Horizontal alignItems="baseline">
        <BackButton onClick={() => setSelectedPageId(null)}>
          ← Back to pages
        </BackButton>

        <Spring />

        {model.isReady.get()
          ? <LoadMoreButton model={model} loadNext={false} loadMorePressed={loadMorePressed} />
          : <Spinner text={ellipsize("Loading")} />
        }

        <Spring />

        <Tooltip align="end" content={
          <HelpText>
            This is the chat between {ProductName} and LLM. {ProductName} requests are shown on the right, LLM responses
            are shown on the left.
          </HelpText>
        }>
          <HelpLight color="var(--secondary-text-color)" />
        </Tooltip>
      </Horizontal>

      <Gap height="8px" />

      {model.logs.get().map(logEntry => (
        <LLMLogEntry key={logEntry.id} log={logEntry} />
      ))}

      <LoadMoreButton model={model} loadNext loadMorePressed={loadMorePressed} />

      {model.isReady.get() && (
        <Footer>
          <FooterTitle>
            Timeline<Spring />{model.logs.get().length > 1 ? `${formatDateTime(model.logs.get()[0].created)}–${formatDateTime(model.logs.get()[model.logs.get().length - 1].created)}` : null}
          </FooterTitle>
          <LLMLogTimeline logs={model.logs.get()} />
          <Horizontal alignItems="center" gap="8px 24px" wrap="wrap">
            <DevModeSwitch>
              <input type="checkbox" checked={model.collapseSystemAndContextMessages.get()}
                     onChange={(e) => runInAction(() => model.collapseSystemAndContextMessages.set(e.target.checked))} />
              Collapse system & context messages
            </DevModeSwitch>
            <DevModeSwitch>
              <input type="checkbox" checked={model.disableAutoscroll.get()}
                     onChange={(e) => runInAction(() => model.disableAutoscroll.set(e.target.checked))} />
              Disable autoscroll
            </DevModeSwitch>

            <SmallActionLink href={grafanaLink(props.model.taskId)} target="_blank" rel="noopener noreferrer">
              Logs in Grafana (prod) {Characters.UpRightArrow}
            </SmallActionLink>
            <SmallActionLink href={taskDashboardLink(props.model.taskId)} target="_blank" rel="noopener noreferrer">
              Task Dashboard (prod) {Characters.UpRightArrow}
            </SmallActionLink>
          </Horizontal>
        </Footer>
      )}
    </Container>
  )
})

const LLMLogTimeline = (props: {
  logs: LLMLogModel[]
}) => {
  const { processedEvents } = useMemo(() => {
    if (props.logs.length === 0) return { processedEvents: [] }

    const times = props.logs.map(log => log.created)
    const minTime = Math.min(...times)
    const maxTime = Math.max(...times)

    const processedEvents = props.logs.map((log, index) => {
      const startTime = log.created
      const endTime = index < props.logs.length - 1 ? times[index + 1] : maxTime

      const width = ((endTime - startTime) / (maxTime - minTime)) * 100
      const left = ((startTime - minTime) / (maxTime - minTime)) * 100

      return ({
        log: log,
        isError: log.errorType != null,
        startTime: startTime,
        endTime: endTime,
        width: isNaN(width) ? 0 : width,
        left: isNaN(left) ? 0 : left,
      })
    })

    return { processedEvents }
  }, [props.logs])

  return (
    <Timeline>
      {processedEvents.map(event => (
        <Tooltip
          key={event.log.id}
          content={`${event.log.role} | ${event.log.label} | ${formatDuration(event.endTime - event.startTime)} till the next event`}
        >
          <TimelineEvent
            key={event.log.id}
            left={event.left}
            width={event.width}
            isError={event.isError}
            onClick={() => document.querySelector(`[data-scroll-to="${event.log.id}"]`)?.scrollIntoView()}
          />
        </Tooltip>
      ))}
    </Timeline>
  )
}

const LLMLogEntry = observer((props: {
  log: LLMLogModel
}) => {
  const isMyMessage = props.log.fromDarkmatter
  const [raw, setRaw] = useState(false)
  const dollarsSpent = useMemo(() => {
    if (props.log.creditsSpent) {
      return formatDollars(creditsToDollars(props.log.creditsSpent))
    } else {
      return null
    }
  }, [props.log.creditsSpent])
  const time = useMemo(() => formatDateTime(props.log.created), [props.log.created])

  const [showFullMessage, setShowFullMessage] = useState(false)

  return (
    <UserMessageContainer
      data-scroll-to={props.log.id}
      isMyMessage={isMyMessage}
      isActive
    >
      <StyledUserMessageBalloon
        isMyMessage={isMyMessage}
        className={props.log.errorType ? Warning : undefined}
      >
        <Vertical>
          <Horizontal gap="8px" alignItems="center" justifyContent="space-between">
            <UserMessageType onClick={() => props.log.toggleCollapsed()}>
              {`${props.log.role} ${dollarsSpent ? `| $${dollarsSpent}` : ""} | ${props.log.label} | ${time}`}
            </UserMessageType>
            <Horizontal gap="8px" alignItems="center">
              {props.log.tokenStats &&
                <TokenStatsTooltip stats={props.log.tokenStats} isCachePoint={props.log.isCachePoint == true} />
              }
              <ShowFullLogEntryTrigger log={props.log} raw={raw} showFullMessage={showFullMessage}
                                       setShowFullMessage={setShowFullMessage} />
              <LogEntryMore log={props.log} setShowRaw={setRaw} showRaw={raw} />
            </Horizontal>
          </Horizontal>

          {!props.log.collapsed.get()
            ? (
              <LogContent log={props.log} raw={raw} showFullMessage={showFullMessage}
                          setShowFullMessage={setShowFullMessage} />
            )
            : null
          }
        </Vertical>
      </StyledUserMessageBalloon>
    </UserMessageContainer>
  )
})

const LogContent = (props: {
  log: LLMLogModel
  raw: boolean
  showFullMessage: boolean
  setShowFullMessage: (value: boolean) => void
}) => {
  return (
    <>
      <MessageText>
        <RawContent>
          {
            props.raw
              ? props.log.message
              : (props.showFullMessage ? props.log.formattedMessage : props.log.shortFormattedMessage)
          }
        </RawContent>
        <ShowFullLogEntryTrigger log={props.log} raw={props.raw} showFullMessage={props.showFullMessage}
                                 setShowFullMessage={props.setShowFullMessage} />
      </MessageText>

      {props.log.error && (
        <ErrorBlock>
          <RawContent>{props.log.error}</RawContent>
        </ErrorBlock>
      )}
    </>
  )
}

const ShowFullLogEntryTrigger = (props: {
  log: LLMLogModel
  raw: boolean
  showFullMessage: boolean
  setShowFullMessage: (value: boolean) => void
}) => {
  if (props.log.collapsed.get() || props.log.formattedMessage.trim() == props.log.shortFormattedMessage.trim() || props.raw) {
    return null
  }

  return (
    <SmallActionLink href="#" onClick={(e) => {
      e.preventDefault()
      props.setShowFullMessage(!props.showFullMessage)
    }}>
      Show {props.showFullMessage ? "less" : "more"}
    </SmallActionLink>
  )
}

const LogEntryMore = observer((props: { log: LLMLogModel, setShowRaw: (value: boolean) => void, showRaw: boolean }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <LogEntryMoreTrigger>
        <MoreRegular className={Icon16} />
      </LogEntryMoreTrigger>
      {dropdownOpen && (
        <DropdownMenuContent align="end">
          {recordLength(props.log.params) > 0 && !props.log.collapsed.get()
            ? (
              <DropdownMenuItem onClick={() => props.setShowRaw(!props.showRaw)}>
                {props.showRaw ? "Show formatted" : "Show raw"}
              </DropdownMenuItem>
            )
            : null
          }
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  )
})

function grafanaLink(taskId: string): string {
  return `https://grafana.intellij.net/explore?schemaVersion=1&panes=%7B%22pt9%22:%7B%22datasource%22:%22c5qeutDVk%22,%22queries%22:%5B%7B%22refId%22:%22A%22,%22expr%22:%22%7Bcluster%3D%5C%22eks-eu-west-1%5C%22%2C%20container%3D%5C%22darkmatter%5C%22%7D%20%7C~%20%5C%22${taskId}%5C%22%20%7C%20json%22,%22queryType%22:%22range%22,%22datasource%22:%7B%22type%22:%22loki%22,%22uid%22:%22c5qeutDVk%22%7D,%22editorMode%22:%22code%22%7D%5D,%22range%22:%7B%22from%22:%22now-1h%22,%22to%22:%22now%22%7D,%22panelsState%22:%7B%22logs%22:%7B%22logs%22:%7B%22visualisationType%22:%22logs%22%7D%7D%7D%7D%7D&orgId=1`
}

function taskDashboardLink(taskId: string): string {
  return `https://grafana.intellij.net/d/benxya8w46qkgd/task-dashboard?orgId=1&var-taskId=${taskId}&var-level=.%2A`
}

const Container = styled.div`
  width: 100%;
`

const LogEntryMoreTrigger = styled(DropdownMenuTrigger)`
  display: flex;
`

const RawContent = styled.pre`
  ${textStyles.pre}
`

const ErrorBlock = styled.div`
  ${textStyles.smallFontSize};
  ${textStyles.breakWord};

  margin: 8px 0;
  padding: 16px;
  border: 1px solid var(--error-color);
  border-radius: var(--border-radius-6);

  color: var(--text-color);
  background-color: var(--error-color-20);
`

const HelpText = styled.div`
  max-width: 320px;
`

const FooterTitle = styled(Horizontal)`
  ${textStyles.smallestFontSize};

  color: var(--text-color-40);
  white-space: nowrap;

  gap: 8px;
`

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  padding: 8px;
  background: linear-gradient(to bottom, var(--text-color-5), var(--text-color-5)), var(--background-color); /* color mixing hack */
  z-index: 1;
`

const Timeline = styled.div`
  position: relative;
  height: 24px;
  width: 100%;

  margin: 8px 0;
  background: var(--main-color-5);
`

const TimelineEvent = styled.div<{
  left: number
  width: number
  isError: boolean
}>`
  position: absolute;
  min-width: ${props => props.isError ? "3px" : "1px"};
  height: 100%;

  background: ${props => props.isError ? "var(--warning-color-80)" : "var(--main-color-20)"};

  &:hover {
    background: ${props => props.isError ? "var(--warning-color)" : "var(--main-color-50)"};
  }

  left: ${props => `${props.left}%`};
  width: ${props => `${props.width}%`};

  z-index: ${props => props.isError ? "1" : "auto"};
`

const DevModeSwitch = styled.label`
  color: var(--text-color);
  font-size: small;
  font-weight: normal;
  white-space: nowrap;
  user-select: none;

  & > input {
    margin-right: 4px;
  }
`

const SmallActionLink = styled.a`
  white-space: nowrap;
  font-size: smaller;
`

const BackButton = styled.button`
  ${textStyles.smallFontSize};
  color: var(--main-color);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--border-radius-4);

  &:hover {
    background-color: var(--text-color-5);
  }
`

const Warning = css`
  background-color: var(--warning-color-10);
`

const TokenStatsTooltip = (props: { stats: LlmTokenStats, isCachePoint: boolean }) => {
  return (
    <Tooltip align="end" content={
      <TokenStatsContent>
        <TokenStatsRow>
          <TokenStatsLabel>Input tokens:</TokenStatsLabel>
          <TokenStatsValue>{props.stats.inputTokens}</TokenStatsValue>
        </TokenStatsRow>

        <TokenStatsRow>
          <TokenStatsLabel>Output tokens:</TokenStatsLabel>
          <TokenStatsValue>{props.stats.outputTokens}</TokenStatsValue>
        </TokenStatsRow>

        {props.stats.cacheReadInputTokens != null && (
          <TokenStatsRow>
            <TokenStatsLabel>Cache read input tokens:</TokenStatsLabel>
            <TokenStatsValue>{props.stats.cacheReadInputTokens}</TokenStatsValue>
          </TokenStatsRow>
        )}
        {props.stats.cacheWriteInputTokens != null && (
          <TokenStatsRow>
            <TokenStatsLabel>Cache write input tokens:</TokenStatsLabel>
            <TokenStatsValue>{props.stats.cacheWriteInputTokens}</TokenStatsValue>
          </TokenStatsRow>
        )}
        {props.isCachePoint && (
          <TokenStatsRow>
            <TokenStatsLabel>Cache point:</TokenStatsLabel>
            <TokenStatsValue>Yes</TokenStatsValue>
          </TokenStatsRow>
        )}
      </TokenStatsContent>
    }>
      <TokenStatsIcon>
        <CodeFileMedium className={Icon16} color="var(--secondary-text-color)" />
      </TokenStatsIcon>
    </Tooltip>
  )
}

const TokenStatsIcon = styled(Horizontal)`
  align-items: center;

  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const TokenStatsContent = styled(Vertical)`
  gap: 4px;
  min-width: 220px;
`

const TokenStatsRow = styled(Horizontal)`
  justify-content: space-between;
  align-items: center;
`

const TokenStatsLabel = styled.span`
  color: var(--text-color-80);
  font-size: 12px;
`

const TokenStatsValue = styled.span`
  color: var(--text-color);
  font-size: 12px;
  font-weight: 500;
`
